import type { CSSProperties, ReactNode } from "react";
import { invoiceTotal, itemTotal } from "./calc";
import { formatMoney } from "./currency";
import type { Invoice, InvoiceItem, InvoiceTemplate } from "./types";

type InvoicePreviewProps = {
  invoice: Invoice;
};

type NormalizedTemplate = InvoiceTemplate;

type TemplateTone = {
  sheet: string;
  ink: string;
  muted: string;
  faint: string;
  rule: string;
  softRule: string;
  wash: string;
  strongWash: string;
};

const tone: TemplateTone = {
  sheet: "#fbfbfb",
  ink: "#151515",
  muted: "#555555",
  faint: "#7a7a7a",
  rule: "#1f1f1f",
  softRule: "#d2d2d2",
  wash: "#f1f1f1",
  strongWash: "#e2e2e2",
};

const ledgerMarks = Array.from({ length: 24 }, (_, index) => `ledger-mark-${index}`);

export function InvoicePreview({ invoice }: InvoicePreviewProps) {
  const template = normalizeTemplate(invoice.template);

  switch (template) {
    case "atelier":
      return <AtelierTemplate invoice={invoice} />;
    case "letterhead":
      return <LetterheadTemplate invoice={invoice} />;
    case "statement":
      return <StatementTemplate invoice={invoice} />;
    case "grid":
      return <GridTemplate invoice={invoice} />;
    case "editorial":
      return <EditorialTemplate invoice={invoice} />;
    case "compact":
      return <CompactTemplate invoice={invoice} />;
    case "receipt":
      return <ReceiptTemplate invoice={invoice} />;
    case "executive":
      return <ExecutiveTemplate invoice={invoice} />;
    case "studio":
      return <StudioTemplate invoice={invoice} />;
    default:
      return <LedgerTemplate invoice={invoice} />;
  }
}

function LedgerTemplate({ invoice }: InvoicePreviewProps) {
  const total = invoiceTotal(invoice);

  return (
    <Sheet mono className="border-2 p-[34px]" style={{ borderColor: tone.rule }}>
      <header className="border-2" style={{ borderColor: tone.rule, background: tone.sheet }}>
        <div
          className="grid grid-cols-[1fr_96px_96px_96px] border-b-2 text-[10px] uppercase tracking-[0.06em]"
          style={{ borderColor: tone.rule }}
        >
          <LedgerCell label="Form" value="NVOICE/BW-001" />
          <LedgerCell label="Size" value="A4" />
          <LedgerCell label="Mode" value="Print" />
          <LedgerCell label="Currency" value={invoice.details.currency} last />
        </div>
        <div className="grid grid-cols-[1fr_172px]">
          <div className="p-5">
            <div className="flex items-center gap-1">
              {["#151515", "#4a4a4a", "#777777", "#a7a7a7", "#c9c9c9", "#e4e4e4"].map((shade) => (
                <span
                  key={shade}
                  className="h-3 w-10 border"
                  style={{ background: shade, borderColor: tone.rule }}
                />
              ))}
            </div>
            <h1 className="mt-8 text-[72px] font-bold leading-[0.82] tracking-[-0.08em]">
              INVOICE
            </h1>
            <p className="mt-4 max-w-[430px] text-[12px] uppercase leading-[1.35] tracking-[0.04em]">
              Monospaced commercial document with tabular figures, hard rules, specimen marks, and
              catalog-grade billing data.
            </p>
          </div>
          <div className="border-l-2 p-4" style={{ borderColor: tone.rule }}>
            <div className="flex justify-end">
              <LogoMark invoice={invoice} />
            </div>
            <div className="mt-8 space-y-2 text-right text-[11px] uppercase tracking-[0.05em]">
              <MonoChip>{invoice.details.invoiceNumber || "INV-0000"}</MonoChip>
              <MonoChip>{formatDate(invoice.details.issueDate)}</MonoChip>
              <MonoChip>Due {formatDate(invoice.details.dueDate)}</MonoChip>
            </div>
          </div>
        </div>
      </header>

      <section className="mt-4 grid grid-cols-2 gap-4">
        <LedgerBlock title="Issuer" code="ISR-001">
          <PartySummary party="business" invoice={invoice} />
        </LedgerBlock>
        <LedgerBlock title="Recipient" code="RCP-001">
          <PartySummary party="client" invoice={invoice} />
        </LedgerBlock>
      </section>

      <section className="mt-4 border-2" style={{ borderColor: tone.rule, background: tone.sheet }}>
        <LedgerTable invoice={invoice} />
      </section>

      <section className="mt-4 grid grid-cols-[1fr_254px] gap-4">
        <LedgerBlock title="Payment / Notes" code="PAY-001">
          <PaymentLines value={paymentText(invoice)} />
        </LedgerBlock>
        <div
          className="border-2 p-4 text-right"
          style={{ borderColor: tone.rule, background: tone.strongWash }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em]">Total amount due</p>
          <p className="mt-4 text-[31px] font-bold tracking-[-0.05em] tabular-nums">
            {formatMoney(total, invoice.details.currency)}
          </p>
          <div className="mt-5 grid grid-cols-8 gap-1">
            {ledgerMarks.map((mark) => (
              <span
                key={mark}
                className="h-2 border bg-[#fbfbfb]"
                style={{ borderColor: tone.rule }}
              />
            ))}
          </div>
        </div>
      </section>

      <footer
        className="mt-4 flex items-center justify-between border-t-2 pt-3 text-[10px] uppercase tracking-[0.06em]"
        style={{ borderColor: tone.rule, color: tone.muted }}
      >
        <span>Black and white commercial specimen</span>
        <span>tabular: {formatMoney(total, invoice.details.currency)}</span>
      </footer>
    </Sheet>
  );
}

function AtelierTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet className="px-[92px] py-[86px]">
      <header className="grid grid-cols-[1fr_120px] gap-12">
        <div>
          <h1 className="text-[26px] font-semibold leading-none tracking-[-0.035em]">Invoice</h1>
          <p className="mt-3 text-[15px] tracking-[-0.015em]" style={{ color: tone.muted }}>
            {invoice.business.name || "Business name"}
          </p>
        </div>
        <div className="flex justify-end">
          <LogoMark invoice={invoice} />
        </div>
      </header>

      <section className="mt-[46px] grid grid-cols-2 gap-16 text-[10.5px] leading-[1.55]">
        <PartyColumn title="From" invoice={invoice} party="business" />
        <PartyColumn title="Bill to" invoice={invoice} party="client" align="right" />
      </section>

      <MetaRow invoice={invoice} className="mt-9" />
      <div className="mt-10 h-px" style={{ background: tone.softRule }} />
      <SimpleItemsTable invoice={invoice} className="mt-9" />
      <TotalBlock invoice={invoice} className="mt-10" />
      <NotesFooter invoice={invoice} className="mt-12" />
    </Sheet>
  );
}

function LetterheadTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet className="px-[74px] py-[68px]">
      <header className="border-y py-7 text-center" style={{ borderColor: tone.rule }}>
        <p className="text-[10px] uppercase tracking-[0.28em]" style={{ color: tone.muted }}>
          {invoice.business.name || "Business name"}
        </p>
        <h1 className="mt-4 text-[42px] font-medium tracking-[-0.045em]">Invoice</h1>
        <p className="mt-4 text-[11px] uppercase tracking-[0.18em]" style={{ color: tone.muted }}>
          {invoice.details.invoiceNumber || "INV-0000"} · Issued{" "}
          {formatDate(invoice.details.issueDate)}
        </p>
      </header>

      <section className="mt-10 grid grid-cols-2 gap-10 text-[11px] leading-[1.65]">
        <PartyColumn title="From" invoice={invoice} party="business" />
        <PartyColumn title="Bill to" invoice={invoice} party="client" align="right" />
      </section>

      <section
        className="mt-10 grid grid-cols-3 border text-[10.5px]"
        style={{ borderColor: tone.rule }}
      >
        <Stat label="Invoice no." value={invoice.details.invoiceNumber || "—"} />
        <Stat label="Issue date" value={formatDate(invoice.details.issueDate)} />
        <Stat label="Due date" value={formatDate(invoice.details.dueDate)} last />
      </section>

      <RuledItemsTable invoice={invoice} className="mt-10" />
      <TotalBlock invoice={invoice} className="mt-8" strong />
      <NotesFooter invoice={invoice} className="mt-12" withLogo />
    </Sheet>
  );
}

function StatementTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet className="px-[64px] py-[60px]">
      <header className="border-b-2 pb-8" style={{ borderColor: tone.rule }}>
        <div className="flex items-start justify-between gap-10">
          <div>
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: tone.muted }}
            >
              Amount due
            </p>
            <h1 className="mt-3 max-w-[520px] text-[62px] font-semibold leading-[0.9] tracking-[-0.075em]">
              {formatMoney(invoiceTotal(invoice), invoice.details.currency)}
            </h1>
          </div>
          <LogoMark invoice={invoice} />
        </div>
        <div className="mt-8 grid grid-cols-3 gap-2 text-[10.5px]">
          <BoxMeta label="Invoice" value={invoice.details.invoiceNumber || "—"} />
          <BoxMeta label="Issued" value={formatDate(invoice.details.issueDate)} />
          <BoxMeta label="Due" value={formatDate(invoice.details.dueDate)} />
        </div>
      </header>

      <section className="mt-8 grid grid-cols-[1fr_1fr] gap-10 text-[11px] leading-[1.5]">
        <PartyColumn title="From" invoice={invoice} party="business" />
        <PartyColumn title="To" invoice={invoice} party="client" />
      </section>

      <BoldItemsTable invoice={invoice} className="mt-9" />
      <NotesFooter invoice={invoice} className="mt-10" />
    </Sheet>
  );
}

function GridTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet className="p-[54px]">
      <header className="grid grid-cols-[1fr_220px] border" style={{ borderColor: tone.rule }}>
        <div className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: tone.muted }}>
            Tax invoice
          </p>
          <h1 className="mt-4 text-[48px] font-semibold leading-none tracking-[-0.055em]">
            {invoice.details.invoiceNumber || "INV-0000"}
          </h1>
        </div>
        <div
          className="border-l p-6 text-right"
          style={{ borderColor: tone.rule, background: tone.wash }}
        >
          <p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: tone.muted }}>
            Total
          </p>
          <p className="mt-3 text-[27px] font-semibold tracking-[-0.045em]">
            {formatMoney(invoiceTotal(invoice), invoice.details.currency)}
          </p>
        </div>
      </header>

      <section
        className="grid grid-cols-4 border-x border-b text-[10.5px]"
        style={{ borderColor: tone.rule }}
      >
        <Stat label="Currency" value={invoice.details.currency} />
        <Stat label="Issued" value={formatDate(invoice.details.issueDate)} />
        <Stat label="Due" value={formatDate(invoice.details.dueDate)} />
        <Stat label="Lines" value={String(invoice.items.length)} last />
      </section>

      <section className="mt-8 grid grid-cols-2 gap-0 border" style={{ borderColor: tone.rule }}>
        <div className="border-r p-5" style={{ borderColor: tone.rule }}>
          <PartyColumn title="From" invoice={invoice} party="business" />
        </div>
        <div className="p-5">
          <PartyColumn title="Bill to" invoice={invoice} party="client" />
        </div>
      </section>

      <GridItemsTable invoice={invoice} className="mt-8" />
      <NotesFooter invoice={invoice} className="mt-8" />
    </Sheet>
  );
}

function EditorialTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet className="px-[70px] py-[62px]">
      <header className="grid grid-cols-[1fr_150px] gap-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: tone.muted }}>
            {invoice.business.name || "Business"}
          </p>
          <h1 className="mt-8 max-w-[440px] text-[58px] font-medium leading-[0.94] tracking-[-0.07em]">
            Invoice for professional services
          </h1>
        </div>
        <div className="text-right text-[10.5px] leading-[1.6]">
          <LogoMark invoice={invoice} />
          <div className="mt-8">
            <InlineDetail label="Invoice" value={invoice.details.invoiceNumber} />
            <InlineDetail label="Issued" value={formatDate(invoice.details.issueDate)} />
            <InlineDetail label="Due" value={formatDate(invoice.details.dueDate)} />
          </div>
        </div>
      </header>

      <section className="mt-12 grid grid-cols-[220px_1fr] gap-12">
        <aside className="text-[10.5px] leading-[1.55]">
          <PartyColumn title="From" invoice={invoice} party="business" />
          <div className="mt-8">
            <PartyColumn title="To" invoice={invoice} party="client" />
          </div>
        </aside>
        <main>
          <SimpleItemsTable invoice={invoice} />
          <TotalBlock invoice={invoice} className="mt-10" />
          <NotesFooter invoice={invoice} className="mt-10" />
        </main>
      </section>
    </Sheet>
  );
}

function CompactTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet mono className="px-[48px] py-[50px]">
      <header
        className="flex items-start justify-between border-b-2 pb-4"
        style={{ borderColor: tone.rule }}
      >
        <div>
          <h1 className="text-[34px] font-bold tracking-[-0.05em]">INVOICE</h1>
          <p className="mt-1 text-[11px] uppercase tracking-[0.08em]" style={{ color: tone.muted }}>
            {invoice.details.invoiceNumber || "INV-0000"}
          </p>
        </div>
        <div className="text-right text-[10.5px] leading-[1.45]">
          <p>{formatDate(invoice.details.issueDate)}</p>
          <p>Due {formatDate(invoice.details.dueDate)}</p>
          <p>{invoice.details.currency}</p>
        </div>
      </header>

      <section className="mt-5 grid grid-cols-2 gap-6 text-[10.5px] leading-[1.45]">
        <PartyColumn title="From" invoice={invoice} party="business" />
        <PartyColumn title="To" invoice={invoice} party="client" />
      </section>

      <DenseItemsTable invoice={invoice} className="mt-6" />
      <section className="mt-5 grid grid-cols-[1fr_220px] gap-6">
        <NotesFooter invoice={invoice} />
        <TotalBox invoice={invoice} />
      </section>
    </Sheet>
  );
}

function ReceiptTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet mono className="px-[150px] py-[58px]">
      <main className="mx-auto min-h-[980px] border px-8 py-8" style={{ borderColor: tone.rule }}>
        <header className="text-center">
          <LogoMark invoice={invoice} />
          <h1 className="mt-6 text-[32px] font-bold tracking-[-0.06em]">INVOICE</h1>
          <p className="mt-2 text-[11px] uppercase tracking-[0.08em]" style={{ color: tone.muted }}>
            {invoice.details.invoiceNumber || "INV-0000"}
          </p>
        </header>
        <div className="my-7 border-t border-dashed" style={{ borderColor: tone.rule }} />
        <PartyColumn title="From" invoice={invoice} party="business" />
        <div className="mt-6">
          <PartyColumn title="To" invoice={invoice} party="client" />
        </div>
        <div className="my-7 border-t border-dashed" style={{ borderColor: tone.rule }} />
        <ReceiptItems invoice={invoice} />
        <div className="my-7 border-t border-dashed" style={{ borderColor: tone.rule }} />
        <TotalBox invoice={invoice} />
        <NotesFooter invoice={invoice} className="mt-8" />
      </main>
    </Sheet>
  );
}

function ExecutiveTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet className="px-[66px] py-[58px]">
      <header className="grid grid-cols-[1fr_220px] gap-10">
        <div>
          <LogoMark invoice={invoice} />
          <h1 className="mt-9 text-[38px] font-semibold tracking-[-0.05em]">Invoice</h1>
          <p className="mt-2 text-[13px]" style={{ color: tone.muted }}>
            Prepared by {invoice.business.name || "your business"}
          </p>
        </div>
        <TotalBox invoice={invoice} />
      </header>

      <section
        className="mt-10 grid grid-cols-[1fr_1fr] gap-6 border-y py-6"
        style={{ borderColor: tone.rule }}
      >
        <PartyColumn title="From" invoice={invoice} party="business" />
        <PartyColumn title="Bill to" invoice={invoice} party="client" />
      </section>

      <MetaRow invoice={invoice} className="mt-7" />
      <ExecutiveItemsTable invoice={invoice} className="mt-9" />
      <NotesFooter invoice={invoice} className="mt-10" />
    </Sheet>
  );
}

function StudioTemplate({ invoice }: InvoicePreviewProps) {
  return (
    <Sheet className="px-[82px] py-[72px]">
      <header className="text-center">
        <LogoMark invoice={invoice} />
        <p className="mt-7 text-[11px] uppercase tracking-[0.24em]" style={{ color: tone.muted }}>
          {invoice.business.name || "Business name"}
        </p>
        <h1 className="mt-5 text-[44px] font-medium tracking-[-0.06em]">Invoice</h1>
      </header>

      <section
        className="mt-12 grid grid-cols-3 gap-0 border-y py-5 text-center"
        style={{ borderColor: tone.rule }}
      >
        <PlainMeta label="Invoice" value={invoice.details.invoiceNumber || "—"} />
        <PlainMeta label="Issued" value={formatDate(invoice.details.issueDate)} />
        <PlainMeta label="Due" value={formatDate(invoice.details.dueDate)} />
      </section>

      <section className="mt-10 grid grid-cols-2 gap-12 text-[11px] leading-[1.6]">
        <PartyColumn title="From" invoice={invoice} party="business" align="center" />
        <PartyColumn title="To" invoice={invoice} party="client" align="center" />
      </section>

      <SimpleItemsTable invoice={invoice} className="mt-12" />
      <TotalBlock invoice={invoice} className="mt-10" center />
      <NotesFooter invoice={invoice} className="mt-12 text-center" />
    </Sheet>
  );
}

function Sheet({
  children,
  className = "",
  mono,
  style,
}: {
  children: ReactNode;
  className?: string;
  mono?: boolean;
  style?: CSSProperties;
}) {
  return (
    <article
      id="invoice-preview"
      className={`print-sheet ${mono ? "print-sheet--mono" : ""} mx-auto min-h-[1123px] w-[794px] border bg-[#fbfbfb] text-[#151515] shadow-2xl shadow-black/10 ${className}`}
      style={{ borderColor: tone.softRule, ...style }}
    >
      {children}
    </article>
  );
}

function PartyColumn({
  title,
  invoice,
  party,
  align = "left",
}: {
  title: string;
  invoice: Invoice;
  party: "business" | "client";
  align?: "left" | "right" | "center";
}) {
  const data = party === "business" ? invoice.business : invoice.client;
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  return (
    <section className={alignClass}>
      <p
        className="mb-3 text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: tone.muted }}
      >
        {title}
      </p>
      <p className="font-semibold">{data.name || "—"}</p>
      {data.email ? (
        <p className="mt-1" style={{ color: tone.muted }}>
          {data.email}
        </p>
      ) : null}
      <AddressLines value={data.address} align={align} />
    </section>
  );
}

function PartySummary({ invoice, party }: { invoice: Invoice; party: "business" | "client" }) {
  const data = party === "business" ? invoice.business : invoice.client;
  return (
    <>
      <p className="text-[16px] font-semibold uppercase tracking-[-0.02em]">{data.name || "—"}</p>
      {data.email ? <p className="mt-1 text-[11px]">{data.email}</p> : null}
      <div className="mt-3 text-[11px] leading-[1.45]" style={{ color: tone.muted }}>
        <AddressLines value={data.address} />
      </div>
    </>
  );
}

function AddressLines({
  value,
  align = "left",
}: {
  value: string;
  align?: "left" | "right" | "center";
}) {
  const lines = splitAddress(value);
  if (lines.length === 0)
    return (
      <p className="mt-1" style={{ color: tone.faint }}>
        —
      </p>
    );
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <div className={`mt-2 ${alignClass}`} style={{ color: tone.muted }}>
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

function MetaRow({ invoice, className = "" }: { invoice: Invoice; className?: string }) {
  return (
    <section className={`grid grid-cols-3 gap-3 text-[10.5px] ${className}`}>
      <BoxMeta label="Invoice" value={invoice.details.invoiceNumber || "—"} />
      <BoxMeta label="Issued" value={formatDate(invoice.details.issueDate)} />
      <BoxMeta label="Due" value={formatDate(invoice.details.dueDate)} />
    </section>
  );
}

function BoxMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="border p-3" style={{ borderColor: tone.rule, background: tone.sheet }}>
      <p
        className="text-[9.5px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: tone.muted }}
      >
        {label}
      </p>
      <p className="mt-1 font-semibold">{value}</p>
    </div>
  );
}

function PlainMeta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[9.5px] uppercase tracking-[0.16em]" style={{ color: tone.muted }}>
        {label}
      </p>
      <p className="mt-1 font-semibold">{value}</p>
    </div>
  );
}

function Stat({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={`p-3 ${last ? "" : "border-r"}`} style={{ borderColor: tone.rule }}>
      <p className="text-[9.5px] uppercase tracking-[0.18em]" style={{ color: tone.muted }}>
        {label}
      </p>
      <p className="mt-1 font-semibold">{value}</p>
    </div>
  );
}

function TotalBlock({
  invoice,
  className = "",
  strong,
  center,
}: {
  invoice: Invoice;
  className?: string;
  strong?: boolean;
  center?: boolean;
}) {
  return (
    <section className={`flex ${center ? "justify-center" : "justify-end"} ${className}`}>
      <div
        className={`${center ? "text-center" : "text-right"} ${strong ? "border-y py-4" : ""}`}
        style={{ borderColor: tone.rule }}
      >
        <p
          className="text-[10px] font-semibold uppercase tracking-[0.16em]"
          style={{ color: tone.muted }}
        >
          Total amount due
        </p>
        <p className="mt-2 text-[27px] font-semibold tracking-[-0.045em] tabular-nums">
          {formatMoney(invoiceTotal(invoice), invoice.details.currency)}
        </p>
      </div>
    </section>
  );
}

function TotalBox({ invoice }: { invoice: Invoice }) {
  return (
    <div
      className="border p-5 text-right"
      style={{ borderColor: tone.rule, background: tone.wash }}
    >
      <p
        className="text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: tone.muted }}
      >
        Total due
      </p>
      <p className="mt-3 text-[25px] font-semibold tracking-[-0.04em] tabular-nums">
        {formatMoney(invoiceTotal(invoice), invoice.details.currency)}
      </p>
    </div>
  );
}

function NotesFooter({
  invoice,
  className = "",
  withLogo,
}: {
  invoice: Invoice;
  className?: string;
  withLogo?: boolean;
}) {
  return (
    <footer
      className={`grid ${withLogo ? "grid-cols-[1fr_120px]" : "grid-cols-1"} gap-10 border-t pt-7 text-[10.5px] leading-[1.6] ${className}`}
      style={{ borderColor: tone.softRule, color: tone.muted }}
    >
      <div>
        <p className="font-semibold" style={{ color: tone.ink }}>
          Payment terms
        </p>
        <PaymentLines value={paymentText(invoice)} />
      </div>
      {withLogo ? (
        <div className="flex justify-end">
          <LogoMark invoice={invoice} />
        </div>
      ) : null}
    </footer>
  );
}

function SimpleItemsTable({ invoice, className = "" }: { invoice: Invoice; className?: string }) {
  return (
    <table className={`w-full border-collapse text-left text-[10.5px] tabular-nums ${className}`}>
      <thead>
        <tr style={{ color: tone.muted }}>
          <th
            className="w-auto border-b pb-2 text-left font-normal"
            style={{ borderColor: tone.softRule }}
          >
            Description
          </th>
          <th
            className="w-[88px] border-b pb-2 text-right font-normal"
            style={{ borderColor: tone.softRule }}
          >
            Qty
          </th>
          <th
            className="w-[112px] border-b pb-2 text-right font-normal"
            style={{ borderColor: tone.softRule }}
          >
            Unit
          </th>
          <th
            className="w-[120px] border-b pb-2 text-right font-normal"
            style={{ borderColor: tone.softRule }}
          >
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item) => (
          <tr key={item.id}>
            <ItemCells item={item} invoice={invoice} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RuledItemsTable({ invoice, className = "" }: { invoice: Invoice; className?: string }) {
  return (
    <table className={`w-full border-collapse text-[10.5px] ${className}`}>
      <thead>
        <tr
          className="border-y text-[9.5px] uppercase tracking-[0.16em]"
          style={{ borderColor: tone.rule, color: tone.muted }}
        >
          <th className="py-3 text-left font-normal">Service</th>
          <th className="w-[78px] py-3 text-right font-normal">Qty</th>
          <th className="w-[116px] py-3 text-right font-normal">Rate</th>
          <th className="w-[122px] py-3 text-right font-normal">Amount</th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item) => (
          <tr key={item.id} className="border-b" style={{ borderColor: tone.softRule }}>
            <ItemCells item={item} invoice={invoice} roomy />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BoldItemsTable({ invoice, className = "" }: { invoice: Invoice; className?: string }) {
  return (
    <table className={`w-full border-collapse text-[10.5px] ${className}`}>
      <thead>
        <tr
          className="text-[9.5px] uppercase tracking-[0.13em] text-[#f7f7f7]"
          style={{ background: tone.ink }}
        >
          <th className="px-3 py-3 text-left font-semibold">Description</th>
          <th className="w-[70px] px-3 py-3 text-right font-semibold">Qty</th>
          <th className="w-[108px] px-3 py-3 text-right font-semibold">Unit</th>
          <th className="w-[122px] px-3 py-3 text-right font-semibold">Total</th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item) => (
          <tr key={item.id} className="border-b" style={{ borderColor: tone.rule }}>
            <ItemCells item={item} invoice={invoice} roomy />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GridItemsTable({ invoice, className = "" }: { invoice: Invoice; className?: string }) {
  return (
    <table
      className={`w-full border-collapse border text-[10.5px] ${className}`}
      style={{ borderColor: tone.rule }}
    >
      <thead>
        <tr
          className="uppercase tracking-[0.12em]"
          style={{ background: tone.wash, color: tone.muted }}
        >
          <th
            className="border px-3 py-3 text-left font-semibold"
            style={{ borderColor: tone.rule }}
          >
            Description
          </th>
          <th
            className="w-[76px] border px-3 py-3 text-right font-semibold"
            style={{ borderColor: tone.rule }}
          >
            Qty
          </th>
          <th
            className="w-[112px] border px-3 py-3 text-right font-semibold"
            style={{ borderColor: tone.rule }}
          >
            Unit
          </th>
          <th
            className="w-[126px] border px-3 py-3 text-right font-semibold"
            style={{ borderColor: tone.rule }}
          >
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item) => (
          <tr key={item.id}>
            <td className="border px-3 py-3" style={{ borderColor: tone.softRule }}>
              <ItemDescription item={item} />
            </td>
            <td
              className="border px-3 py-3 text-right tabular-nums"
              style={{ borderColor: tone.softRule }}
            >
              {formatQuantity(item.quantity)}
            </td>
            <td
              className="border px-3 py-3 text-right tabular-nums"
              style={{ borderColor: tone.softRule }}
            >
              {formatMoney(item.unitPrice, invoice.details.currency)}
            </td>
            <td
              className="border px-3 py-3 text-right font-semibold tabular-nums"
              style={{ borderColor: tone.softRule }}
            >
              {formatMoney(itemTotal(item), invoice.details.currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DenseItemsTable({ invoice, className = "" }: { invoice: Invoice; className?: string }) {
  return (
    <table
      className={`w-full border-collapse border text-[10px] ${className}`}
      style={{ borderColor: tone.rule }}
    >
      <thead>
        <tr style={{ background: tone.wash }}>
          <th className="border px-2 py-2 text-left" style={{ borderColor: tone.rule }}>
            No
          </th>
          <th className="border px-2 py-2 text-left" style={{ borderColor: tone.rule }}>
            Item
          </th>
          <th className="border px-2 py-2 text-right" style={{ borderColor: tone.rule }}>
            Qty
          </th>
          <th className="border px-2 py-2 text-right" style={{ borderColor: tone.rule }}>
            Unit
          </th>
          <th className="border px-2 py-2 text-right" style={{ borderColor: tone.rule }}>
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item, index) => (
          <tr key={item.id}>
            <td className="border px-2 py-2" style={{ borderColor: tone.softRule }}>
              {String(index + 1).padStart(2, "0")}
            </td>
            <td className="border px-2 py-2" style={{ borderColor: tone.softRule }}>
              <ItemDescription item={item} />
            </td>
            <td
              className="border px-2 py-2 text-right tabular-nums"
              style={{ borderColor: tone.softRule }}
            >
              {formatQuantity(item.quantity)}
            </td>
            <td
              className="border px-2 py-2 text-right tabular-nums"
              style={{ borderColor: tone.softRule }}
            >
              {formatMoney(item.unitPrice, invoice.details.currency)}
            </td>
            <td
              className="border px-2 py-2 text-right font-semibold tabular-nums"
              style={{ borderColor: tone.softRule }}
            >
              {formatMoney(itemTotal(item), invoice.details.currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ExecutiveItemsTable({
  invoice,
  className = "",
}: {
  invoice: Invoice;
  className?: string;
}) {
  return (
    <table className={`w-full border-collapse text-[11px] ${className}`}>
      <tbody>
        {invoice.items.map((item) => (
          <tr key={item.id} className="border-b" style={{ borderColor: tone.softRule }}>
            <td className="py-5 pr-6">
              <ItemDescription item={item} />
            </td>
            <td className="w-[80px] py-5 text-right tabular-nums">
              {formatQuantity(item.quantity)}
            </td>
            <td className="w-[120px] py-5 text-right tabular-nums">
              {formatMoney(item.unitPrice, invoice.details.currency)}
            </td>
            <td className="w-[132px] py-5 text-right font-semibold tabular-nums">
              {formatMoney(itemTotal(item), invoice.details.currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReceiptItems({ invoice }: InvoicePreviewProps) {
  return (
    <div className="text-[10.5px]">
      {invoice.items.map((item) => (
        <div key={item.id} className="grid grid-cols-[1fr_auto] gap-4 py-2">
          <div>
            <p className="font-semibold uppercase">{item.description || "Service"}</p>
            {item.details ? (
              <p className="mt-1" style={{ color: tone.muted }}>
                {item.details}
              </p>
            ) : null}
            <p className="mt-1 tabular-nums" style={{ color: tone.muted }}>
              {formatQuantity(item.quantity)} ×{" "}
              {formatMoney(item.unitPrice, invoice.details.currency)}
            </p>
          </div>
          <p className="font-semibold tabular-nums">
            {formatMoney(itemTotal(item), invoice.details.currency)}
          </p>
        </div>
      ))}
    </div>
  );
}

function LedgerTable({ invoice }: InvoicePreviewProps) {
  return (
    <>
      <div
        className="grid grid-cols-[72px_1fr_82px_112px_122px] border-b-2 text-[10px] font-semibold uppercase tracking-[0.05em]"
        style={{ borderColor: tone.rule, background: tone.wash }}
      >
        <div className="border-r px-2 py-2" style={{ borderColor: tone.rule }}>
          SKU
        </div>
        <div className="border-r px-2 py-2" style={{ borderColor: tone.rule }}>
          Description
        </div>
        <div className="border-r px-2 py-2 text-right" style={{ borderColor: tone.rule }}>
          Qty
        </div>
        <div className="border-r px-2 py-2 text-right" style={{ borderColor: tone.rule }}>
          Unit
        </div>
        <div className="px-2 py-2 text-right">Total</div>
      </div>
      {invoice.items.map((item, index) => (
        <div
          key={item.id}
          className="grid min-h-[54px] grid-cols-[72px_1fr_82px_112px_122px] border-b border-dashed text-[11px] last:border-b-0"
          style={{ borderColor: tone.faint }}
        >
          <div className="border-r px-2 py-2" style={{ borderColor: tone.softRule }}>
            <MonoChip>LI-{String(index + 1).padStart(3, "0")}</MonoChip>
          </div>
          <div className="border-r px-2 py-2" style={{ borderColor: tone.softRule }}>
            <ItemDescription item={item} />
          </div>
          <div
            className="border-r px-2 py-2 text-right tabular-nums"
            style={{ borderColor: tone.softRule }}
          >
            {formatQuantity(item.quantity)}
          </div>
          <div
            className="border-r px-2 py-2 text-right tabular-nums"
            style={{ borderColor: tone.softRule }}
          >
            {formatMoney(item.unitPrice, invoice.details.currency)}
          </div>
          <div className="px-2 py-2 text-right font-semibold tabular-nums">
            {formatMoney(itemTotal(item), invoice.details.currency)}
          </div>
        </div>
      ))}
    </>
  );
}

function ItemCells({
  item,
  invoice,
  roomy,
}: {
  item: InvoiceItem;
  invoice: Invoice;
  roomy?: boolean;
}) {
  const y = roomy ? "py-4" : "py-2";
  return (
    <>
      <td className={`${y} pr-5`}>
        <ItemDescription item={item} />
      </td>
      <td className={`${y} text-right tabular-nums`}>{formatQuantity(item.quantity)}</td>
      <td className={`${y} text-right tabular-nums`}>
        {formatMoney(item.unitPrice, invoice.details.currency)}
      </td>
      <td className={`${y} text-right font-semibold tabular-nums`}>
        {formatMoney(itemTotal(item), invoice.details.currency)}
      </td>
    </>
  );
}

function ItemDescription({ item }: { item: InvoiceItem }) {
  return (
    <>
      <p className="font-semibold tracking-[-0.01em]">{item.description || "Service"}</p>
      {item.details ? (
        <p className="mt-1 text-[10px]" style={{ color: tone.muted }}>
          {item.details}
        </p>
      ) : null}
    </>
  );
}

function LedgerBlock({
  title,
  code,
  children,
}: {
  title: string;
  code: string;
  children: ReactNode;
}) {
  return (
    <div className="border-2" style={{ borderColor: tone.rule, background: tone.sheet }}>
      <div
        className="flex items-center justify-between border-b-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.05em]"
        style={{ borderColor: tone.rule, background: tone.wash }}
      >
        <span>{title}</span>
        <MonoChip>{code}</MonoChip>
      </div>
      <div className="min-h-[104px] p-3 text-[11px] leading-[1.5]">{children}</div>
    </div>
  );
}

function LedgerCell({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={`px-2 py-2 ${last ? "" : "border-r-2"}`} style={{ borderColor: tone.rule }}>
      <span style={{ color: tone.muted }}>{label}: </span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function MonoChip({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex border px-1.5 py-0.5 font-semibold"
      style={{ borderColor: tone.rule, background: tone.wash }}
    >
      {children}
    </span>
  );
}

function InlineDetail({ label, value }: { label: string; value: string }) {
  return (
    <p>
      <span className="font-semibold">{label}: </span>
      <span>{value || "—"}</span>
    </p>
  );
}

function PaymentLines({ value }: { value: string }) {
  const lines = value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return <p>Payment instructions not provided.</p>;

  return (
    <div>
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

function LogoMark({ invoice }: InvoicePreviewProps) {
  if (invoice.business.logo) {
    return (
      <div className="relative mx-auto h-16 w-28 overflow-hidden grayscale">
        <img
          src={invoice.business.logo}
          alt="Logo"
          className="absolute top-1/2 left-1/2 max-h-full max-w-full object-contain"
          style={logoTransformStyle(invoice)}
        />
      </div>
    );
  }

  return (
    <div aria-hidden="true" className="relative mx-auto size-9">
      <div className="absolute top-1 left-0 size-5 rounded-full bg-[#151515]" />
      <div className="absolute right-0 bottom-1 size-5 rounded-full bg-[#151515]" />
      <div className="absolute top-1/2 left-1/2 h-10 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[#fbfbfb]" />
    </div>
  );
}

function normalizeTemplate(template: Invoice["template"]): NormalizedTemplate {
  if (
    template === "atelier" ||
    template === "letterhead" ||
    template === "statement" ||
    template === "grid" ||
    template === "editorial" ||
    template === "compact" ||
    template === "receipt" ||
    template === "executive" ||
    template === "studio"
  ) {
    return template;
  }
  return "ledger";
}

function paymentText(invoice: Invoice) {
  return [invoice.paymentInstructions, invoice.notes].filter(Boolean).join("\n");
}

function splitAddress(value: string) {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function logoTransformStyle(invoice: Invoice): CSSProperties {
  const zoom = invoice.business.logoZoom ?? 100;
  const x = invoice.business.logoX ?? 0;
  const y = invoice.business.logoY ?? 0;

  return {
    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${zoom / 100})`,
  };
}

function formatDate(value: string) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB");
}

function formatQuantity(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
